import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Code as CodeIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
// @ts-ignore
import DOMPurify from 'dompurify';
import { ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';

interface HTMLViewerProps {
  contentId: string;
  metadata: {
    fileName: string;
    mimeType: string;
    fileSize: number;
    contentType: string;
  };
  onError?: (error: ViewerError) => void;
  onLoad?: () => void;
  className?: string;
  style?: React.CSSProperties;
  allowScripts?: boolean; // Default to false for security
  allowExternalResources?: boolean; // Default to false for security
}

const HTMLViewer: React.FC<HTMLViewerProps> = ({
  contentId,
  metadata,
  onError,
  onLoad,
  className,
  style,
  allowScripts = true,
  allowExternalResources = true,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [sanitizedContent, setSanitizedContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'rendered' | 'source'>('rendered');
  const [showSanitizationWarning, setShowSanitizationWarning] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { contentUrl, isDirectDownload, metadata: streamingMetadata } = useDirectContent(contentId, undefined, metadata as any);

  // Configure DOMPurify based on security settings
  const configurePurifyOptions = useCallback(() => {
    const options: any = {
      ADD_TAGS: [],
      ADD_ATTR: [],
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      SAFE_FOR_TEMPLATES: true,
      WHOLE_DOCUMENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false,
      FORCE_BODY: false,
      SANITIZE_DOM: true,
      KEEP_CONTENT: true,
    };

    if (allowScripts) {
      options.ADD_TAGS = ['script'];
      options.ADD_ATTR = ['onclick', 'onload', 'onerror'];
    }

    if (allowExternalResources) {
      options.ADD_ATTR = [...(options.ADD_ATTR || []), 'src', 'href'];
    } else {
      // Remove external resources
      options.FORBID_TAGS = ['img', 'video', 'audio', 'iframe', 'object', 'embed'];
      options.FORBID_ATTR = ['src', 'href', 'action'];
    }

    return options;
  }, [allowScripts, allowExternalResources]);

  // Load HTML content
  useEffect(() => {
    const loadHTML = async () => {
      if (!contentUrl) return;

      setLoading(true);
      try {
        console.log('Loading HTML content:', contentId, 'via', isDirectDownload ? 'presigned URL' : 'streaming');

        // Fetch content from URL (works for both presigned and streaming URLs)
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const text = await response.text();
        setHtmlContent(text);
        
        // Sanitize the HTML
        const purifyOptions = configurePurifyOptions();
        const clean = DOMPurify.sanitize(text, purifyOptions).toString();
        setSanitizedContent(clean);
        
        // Check if content was modified during sanitization
        if (clean !== text && !allowScripts) {
          setShowSanitizationWarning(true);
          console.warn('HTML content was sanitized. Some elements may have been removed for security.');
        }
        
        onLoad?.();
      } catch (error: any) {
        console.error('Failed to load HTML:', error);
        const viewerError: ViewerError = {
          code: 'STREAMING_ERROR',
          message: 'Failed to load HTML content',
          details: error,
          retryable: true,
        };
        onError?.(viewerError);
      } finally {
        setLoading(false);
      }
    };

    loadHTML();
  }, [contentId, contentUrl, isDirectDownload, onLoad, onError, configurePurifyOptions, allowScripts]);

  // Update iframe content when sanitized content changes
  useEffect(() => {
    if (iframeRef.current && sanitizedContent && viewMode === 'rendered') {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(sanitizedContent);
        doc.close();
        
        // Add base styles to iframe
        const style = doc.createElement('style');
        style.textContent = `
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 100%;
            margin: 0;
            padding: 20px;
            box-sizing: border-box;
          }
          img, video, embed, object, iframe {
            max-width: 100%;
            height: auto;
          }
          pre {
            overflow-x: auto;
            background: #f4f4f4;
            padding: 10px;
            border-radius: 4px;
          }
          code {
            background: #f4f4f4;
            padding: 2px 4px;
            border-radius: 2px;
          }
        `;
        doc.head.appendChild(style);
      }
    }
  }, [sanitizedContent, viewMode]);

  // Copy HTML source to clipboard
  const handleCopySource = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(htmlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy HTML:', error);
    }
  }, [htmlContent]);

  if (loading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading HTML content...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* HTML Viewer Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'grey.50',
        }}
      >
        {/* Left side - Info and Security Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            HTML • {htmlContent.length} chars
          </Typography>

          {/* Security indicator */}
          <Tooltip title={allowScripts ? "Scripts allowed (less secure)" : "Scripts blocked (secure)"}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SecurityIcon
                fontSize="small"
                sx={{
                  color: (theme) => {
                    if (allowScripts) return theme.palette.warning.main;
                    return theme.palette.mode === 'dark' ? '#4ade80' : theme.palette.success.main;
                  }
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => {
                    if (allowScripts) return theme.palette.warning.main;
                    return theme.palette.mode === 'dark' ? '#4ade80' : theme.palette.success.main;
                  }
                }}
              >
                {allowScripts ? 'Scripts Enabled' : 'Sanitized'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Right side - View Mode Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'action.selected',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <ToggleButton value="rendered">
              <Tooltip title="Rendered View">
                <VisibilityOffIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="source">
              <Tooltip title="Source Code">
                <CodeIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {viewMode === 'source' && (
            <Tooltip title={copied ? "Copied!" : "Copy Source"}>
              <ToggleButton
                value="copy"
                selected={false}
                onChange={handleCopySource}
                size="small"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </ToggleButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Sanitization Warning — only shown when scripts have been explicitly disabled */}
      {showSanitizationWarning && !allowScripts && (
        <Alert
          severity="info"
          icon={<WarningIcon />}
          onClose={() => setShowSanitizationWarning(false)}
          sx={{ mx: 1, mt: 1 }}
        >
          Some content was removed for security because script execution is disabled.
        </Alert>
      )}

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {viewMode === 'rendered' ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: 'white',
              border: 'none',
            }}
          >
            <iframe
              ref={iframeRef}
              title="HTML Content"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              sandbox={
                allowScripts
                  ? 'allow-scripts allow-same-origin'
                  : 'allow-same-origin'
              }
            />
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              m: 1,
              height: 'calc(100% - 16px)',
              overflow: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Box
              component="pre"
              sx={{
                margin: 0,
                padding: 2,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '14px',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'text.primary',
              }}
            >
              <code>{htmlContent}</code>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default HTMLViewer;